<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-6 align-center">
        <h2>{{ isNaccuUser ? $t('memberDataSection.registerFactory') : $t('memberDataSection.registerBuyingStation') }}</h2>
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" class="mr-3" @click="handleCancel" height="36">{{ $t('back') }}</v-btn>
        <v-btn color="primary" dark height="36" :loading="submitting" :disabled="submitting || !!stationId" @click="handleRegister">{{ $t('memberDataSection.register') }}</v-btn>
      </div>

      <!-- Main Form -->
      <v-card elevation="0" class="px-5 mb-6">
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation autocomplete="off">
            <!-- Station Fields -->
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <label class="field-label">{{ isNaccuUser ? $t('memberDataSection.factoryName') : $t('memberDataSection.buyingStationName') }}<span class="red--text">*</span></label>
                <v-text-field v-model="form.name" outlined dense :placeholder="$t('enterHere')" :rules="[v => !!v || $t('memberDataSection.nameRequired')]"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <label class="field-label">{{ isNaccuUser ? $t('memberDataSection.factoryNumber') : $t('memberDataSection.buyingStationId') }}<span class="red--text">*</span></label>
                <v-text-field v-model="form.stationId" outlined dense :placeholder="$t('enterHere')" :rules="[v => !!v || $t('memberDataSection.factoryNumberRequired')]"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <label class="field-label">{{ $t('memberDataSection.location') }}</label>
                <v-text-field v-model="form.location" outlined dense readonly background-color="grey lighten-4" class="location-field cursor-pointer" :placeholder="$t('memberDataSection.selectOnMap')" @click="openMapDialog">
                  <template v-slot:append><v-icon @click="openMapDialog" color="primary">mdi-map-marker</v-icon></template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <label class="field-label">{{ $t('address') }}</label>
                <v-text-field v-model="form.address" outlined dense :placeholder="$t('enterHere')"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <label class="field-label">{{ $t('memberDataSection.email') }}<span class="red--text">*</span></label>
                <v-text-field v-model="form.email" outlined dense :placeholder="$t('enterHere')" :rules="[v => !!v || $t('memberDataSection.emailRequired'), v => /.+@.+\..+/.test(v) || $t('memberDataSection.invalidEmail')]"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <label class="field-label">{{ $t('memberDataSection.phoneNumber') }}</label>
                <v-text-field v-model="form.phoneNumber" outlined dense :placeholder="$t('enterHere')"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <label class="field-label">{{ $t('memberDataSection.society') }}<span class="red--text">*</span></label>
                <v-autocomplete v-model="form.societyId" :items="societies" item-text="label" item-value="id" outlined dense clearable :placeholder="$t('memberDataSection.makeASelection')" :loading="societiesLoading" :rules="[v => !!v || $t('required')]"></v-autocomplete>
              </v-col>
            </v-row>

            <!-- Add Picture(s) -->
            <v-row>
              <v-col cols="12">
                <label class="field-label d-block mb-2">{{ $t('memberDataSection.addPictures') }}</label>
                <div class="picture-upload-box d-flex flex-column align-center justify-center" @click="$refs.logoInput.click()">
                  <img v-if="logoPreview" :src="logoPreview" class="logo-preview-img" />
                  <template v-else>
                    <v-icon size="32" color="grey darken-1">mdi-camera-outline</v-icon>
                    <v-btn class="add-image-btn mt-2" small depressed @click.stop="$refs.logoInput.click()">{{ $t('memberDataSection.addImages') }}</v-btn>
                    <span class="caption grey--text mt-2">{{ $t('memberDataSection.addImagesOrVideos') }}</span>
                  </template>
                  <input ref="logoInput" type="file" accept="image/*" style="display:none" @change="onLogoChange" />
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>

      <!-- Connected Users -->
      <v-card elevation="0" class="px-5">
        <v-card-title class="d-flex align-center pb-2">
          <span class="text-h6">{{ isNaccuUser ? $t('memberDataSection.factoryUsers') : $t('memberDataSection.connectedUsers') }}</span>
          <v-spacer></v-spacer>
          <v-tooltip top :disabled="!!stationId">
            <template v-slot:activator="{ on, attrs }">
              <span v-on="on" v-bind="attrs">
                <v-btn color="primary" dark :disabled="!stationId" @click="addUserDialog = true">{{ $t('memberDataSection.addNewUser') }}</v-btn>
              </span>
            </template>
            <span>Save the station first</span>
          </v-tooltip>
        </v-card-title>
        <v-card-text>
          <v-row class="align-end mb-2" no-gutters>
            <v-col cols="12" sm="3" class="pr-3 mb-2 mb-sm-0">
              <label class="field-label invisible">{{ $t('search') }}</label>
              <v-text-field v-model="userSearch" prepend-inner-icon="mdi-magnify" outlined dense :placeholder="$t('search')" hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="2" class="pr-3 mb-2 mb-sm-0">
              <label class="field-label">{{ $t('memberDataSection.userRole') }}</label>
              <v-select v-model="filterUserRole" :items="userRoleFilterItems" outlined dense hide-details></v-select>
            </v-col>
            <v-col cols="12" sm="2" class="pr-3">
              <label class="field-label">{{ $t('memberDataSection.accessLevel') }}</label>
              <v-select v-model="filterAccessLevel" :items="accessLevelFilterItems" outlined dense hide-details></v-select>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto">
              <span class="body-2">{{ tableItems.length }} {{ $t('memberDataSection.users') }}</span>
            </v-col>
          </v-row>
          <v-data-table :headers="userTableHeaders" :items="tableItems" :items-per-page="10" hide-default-footer item-key="id" :item-class="rowClass">
            <template #item.nameAndId="{ item }">
              <div>
                <div>{{ item.fullName || [item.firstName, item.lastName].filter(Boolean).join(' ') }}</div>
                <div class="caption grey--text">{{ item.id_number || item.id }}</div>
                <span v-if="item.isOwner" class="primary-user-label">{{ $t('memberDataSection.primaryUser') }}</span>
              </div>
            </template>
            <template #item.userRole>
              Farmer
            </template>
            <template #item.accessLevel="{ item }">
              <v-select
                :key="`acc-${item.id}-${item.accessLevel}-${accessLevelSelectKey}`"
                :value="item.accessLevel"
                :items="item.isOwner ? ownerAccessLevelItems : accessLevelItems"
                :disabled="item.isOwner"
                outlined
                dense
                hide-details
                style="max-width:150px"
                @change="(v) => promptAccessLevelChange(item, v)"
              ></v-select>
            </template>
            <template #item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template #item.active="{ item }">
              <v-btn small rounded depressed min-width="100" height="34" :class="item.active ? 'green-button' : 'red-button'">
                {{ item.active ? $t('memberDataSection.active') : $t('memberDataSection.inactive') }}
              </v-btn>
            </template>
            <template #item.actions="{ item }">
              <v-menu v-if="!item.isOwner" bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="removeConnectedUser(item)">
                    <v-list-item-title class="red--text">{{ $t('memberDataSection.remove') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <template #no-data>
              <div class="text-center py-8 grey--text">{{ $t('memberDataSection.noConnectedUsers') }}</div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Map Dialog -->
    <v-dialog v-model="mapDialog" max-width="700" @click:outside="mapDialog = false">
      <v-card rounded="0">
        <v-toolbar color="primary" flat class="white--text">
          <v-toolbar-title class="font-weight-bold">{{ $t('memberDataSection.selectLocation') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon class="white--text" @click="mapDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pa-4">
          <div ref="mapContainer" class="grey lighten-2" style="width:100%;height:400px;border-radius:4px;"></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outlined color="primary" @click="mapDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" @click="confirmLocation">{{ $t('confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AddFactoryUser
      :dialog="addUserDialog"
      :buyingStationId="stationId"
      @close="addUserDialog = false"
      @userAdded="handleUserAdded"
    />

    <ConfirmBox
      :dialog="accessLevelConfirm.show"
      :title="$t('memberDataSection.changeAccessLevel')"
      :message="accessLevelConfirmMessage"
      :cancelBtnText="$t('cancel')"
      :confirmBtnText="$t('confirm')"
      @cancel:action="cancelAccessLevelChange"
      @continue:action="confirmAccessLevelChange"
    />
  </div>
</template>

<script>
import RoleMixin from "@/mixins/RoleMixin";
import GetPermittedActions from "@/mixins/GetPermittedActions";
import { isKenyaClient } from "@/utils";
import BuyingStationService from "@/_services/BuyingStationService";
import UserService from "@/_services/UserService";
import AddFactoryUser from "@/views/user/naccu-user/AddFactoryUser.vue";
import ConfirmBox from "@/components/ConfirmBox";

export default {
  mixins: [RoleMixin, GetPermittedActions],
  components: {
    AddFactoryUser,
    ConfirmBox,
  },
  // Remember whether we arrived from another in-app page so Back pops history instead of
  // pushing a new list entry (which stacks up and makes browser-Back feel like a loop).
  beforeRouteEnter(to, from, next) {
    next((vm) => { vm.cameFromApp = !!(from && from.name); });
  },
  created() {
    this.fetchSocieties();
  },
  data() {
    return {
      cameFromApp: false,
      valid: true,
      submitting: false,
      stationId: null,
      form: {
        name: '', stationId: '', location: '', lat: null, lng: null,
        address: '', email: '', phoneNumber: '', societyId: null,
      },
      logoPreview: null,
      logoFile: null,
      societies: [],
      societiesLoading: false,
      mapDialog: false,
      mapInstance: null,
      mapMarker: null,
      selectedLat: null,
      selectedLng: null,
      connectedUsers: [],
      userSearch: '',
      filterUserRole: '',
      filterAccessLevel: '',
      addUserDialog: false,
      accessLevelConfirm: { show: false, item: null, level: null },
      accessLevelSelectKey: 0,
    };
  },
  computed: {
    isNaccuUser() { return isKenyaClient(); },
    breadcrumbs() {
      return [
        { text: this.isNaccuUser ? this.$t('memberDataSection.factories') : this.$t('memberDataSection.buyingStation'), disabled: false, to: { name: 'CoffeeBuyingStation' } },
        { text: this.isNaccuUser ? this.$t('memberDataSection.registerFactory') : this.$t('memberDataSection.registerBuyingStation'), disabled: true },
      ];
    },
    userTableHeaders() {
      return [
        { text: this.$t('memberDataSection.userNameAndId'), value: 'nameAndId', sortable: false },
        { text: this.$t('memberDataSection.email'), value: 'email', sortable: false },
        { text: this.$t('memberDataSection.userRole'), value: 'userRole', sortable: false },
        { text: this.$t('memberDataSection.accessLevel'), value: 'accessLevel', sortable: false },
        { text: this.$t('memberDataSection.userRegistrationDate'), value: 'createdAt', sortable: false },
        { text: this.$t('memberDataSection.status'), value: 'active', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'right' },
      ];
    },
    ownerRow() {
      return {
        id: 'owner-row',
        isOwner: true,
        fullName: this.form.name,
        email: this.form.email,
        id_number: this.form.stationId,
        createdAt: null,
        active: true,
        accessLevel: 'owner',
      };
    },
    ownerAccessLevelItems() {
      return [{ text: 'Owner', value: 'owner' }];
    },
    accessLevelItems() {
      return [
        { text: 'Owner', value: 'owner' },
        { text: 'Admin', value: 'admin' },
        { text: 'Clerk', value: 'clerk' },
      ];
    },
    accessLevelFilterItems() {
      return [{ text: 'All', value: '' }, ...this.accessLevelItems];
    },
    accessLevelConfirmMessage() {
      const item = this.accessLevelConfirm.item;
      if (!item) return '';
      const opt = this.accessLevelItems.find(o => o.value === this.accessLevelConfirm.level);
      return this.$t('memberDataSection.changeAccessLevelConfirm', {
        name: item.fullName || item.email,
        level: (opt && opt.text) || this.accessLevelConfirm.level,
      });
    },
    userRoleFilterItems() {
      const roles = new Set(this.connectedUsers.map(u => u.userRole || 'Farmer'));
      return [{ text: 'All', value: '' }, ...Array.from(roles).map(r => ({ text: r, value: r }))];
    },
    filteredConnectedUsers() {
      let list = this.connectedUsers;
      if (this.userSearch) {
        const q = this.userSearch.toLowerCase();
        list = list.filter(u =>
          (u.fullName || '').toLowerCase().includes(q) ||
          (u.firstName || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q)
        );
      }
      if (this.filterUserRole) {
        list = list.filter(u => (u.userRole || 'Farmer') === this.filterUserRole);
      }
      if (this.filterAccessLevel) {
        list = list.filter(u => u.accessLevel === this.filterAccessLevel);
      }
      return list;
    },
    tableItems() {
      return this.stationId ? [this.ownerRow, ...this.filteredConnectedUsers] : this.filteredConnectedUsers;
    },
  },
  methods: {
    async fetchSocieties() {
      this.societiesLoading = true;
      try {
        const response = await UserService.getSocieties();
        const list = (response && response.data) || response || [];
        this.societies = list.map((s) => ({
          id: s.id,
          label: s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.email,
        }));
      } catch (e) {
        this.societies = [];
      } finally {
        this.societiesLoading = false;
      }
    },
    rowClass(item) {
      return item.isOwner ? 'owner-row' : '';
    },
    onLogoChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.logoFile = file;
      this.logoPreview = URL.createObjectURL(file);
    },
    formatDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleDateString();
    },
    handleCancel() {
      // Pop history if we came from within the app; fall back to the list for direct links.
      if (this.cameFromApp) this.$router.back();
      else this.$router.push({ name: 'CoffeeBuyingStation' });
    },
    async handleRegister() {
      if (!this.$refs.form.validate()) return;
      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append('name', this.form.name);
        if (this.form.stationId) fd.append('stationId', this.form.stationId);
        if (this.form.address) fd.append('address', this.form.address);
        if (this.form.email) fd.append('email', this.form.email);
        if (this.form.phoneNumber) fd.append('phoneNumber', this.form.phoneNumber);
        if (this.form.societyId) fd.append('societyId', this.form.societyId);
        if (this.logoFile) fd.append('logo', this.logoFile);
        const data = await BuyingStationService.createBuyingStation(fd);
        this.stationId = data.data.id;
        this.$notify({ text: this.isNaccuUser ? this.$t('memberDataSection.factoryRegisteredSuccessfully') : this.$t('memberDataSection.buyingStationRegisteredSuccessfully'), type: 'success' });
      } catch (err) {
        this.$notify({ text: err?.data?.message || this.$t('memberDataSection.registrationFailed'), type: 'error' });
      } finally {
        this.submitting = false;
      }
    },
    openMapDialog() { this.mapDialog = true; this.$nextTick(() => this.initMap()); },
    initMap() {
      if (!window.google || !this.$refs.mapContainer) return;
      const center = { lat: this.selectedLat || 1.2921, lng: this.selectedLng || 36.8219 };
      this.mapInstance = new window.google.maps.Map(this.$refs.mapContainer, { center, zoom: 6 });
      this.mapMarker = new window.google.maps.Marker({ position: center, map: this.mapInstance, draggable: true });
      this.mapInstance.addListener('click', (e) => {
        this.selectedLat = e.latLng.lat();
        this.selectedLng = e.latLng.lng();
        this.mapMarker.setPosition(e.latLng);
      });
      this.mapMarker.addListener('dragend', (e) => {
        this.selectedLat = e.latLng.lat();
        this.selectedLng = e.latLng.lng();
      });
    },
    confirmLocation() {
      if (this.selectedLat && this.selectedLng) {
        this.form.lat = this.selectedLat;
        this.form.lng = this.selectedLng;
        this.form.location = `${this.selectedLat.toFixed(6)}, ${this.selectedLng.toFixed(6)}`;
      }
      this.mapDialog = false;
    },
    removeConnectedUser(item) {
      this.connectedUsers = this.connectedUsers.filter(u => u.id !== item.id);
    },
    promptAccessLevelChange(item, level) {
      if (!level || item.accessLevel === level) return;
      this.accessLevelConfirm = { show: true, item, level };
    },
    confirmAccessLevelChange() {
      const { item, level } = this.accessLevelConfirm;
      this.accessLevelConfirm = { show: false, item: null, level: null };
      // In-memory only — the factory isn't registered yet, so there's no server-side persistence.
      if (item && level) {
        item.accessLevel = level;
        this.connectedUsers = [...this.connectedUsers];
      }
    },
    cancelAccessLevelChange() {
      // Bump the select key so the dropdown remounts and snaps back to the unchanged accessLevel
      // (Vuetify keeps its own internal selection after a pick; re-rendering alone won't reset it).
      this.accessLevelConfirm = { show: false, item: null, level: null };
      this.accessLevelSelectKey++;
    },
    handleUserAdded(res) {
      this.addUserDialog = false;
      if (res && res.success) {
        this.$notify({ text: res.message, type: 'success' });
        if (res.user) this.connectedUsers.push({ ...res.user, accessLevel: res.user.accessLevel });
      } else {
        this.$notify({ text: (res && res.message) || this.$t('memberDataSection.registrationFailed'), type: 'error' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.field-label { display: block; font-size: 14px; margin-bottom: 4px; color: rgba(0,0,0,0.7); }
.field-label.invisible { visibility: hidden; }
.picture-upload-box {
  width: 300px; min-height: 140px;
  border: 2px dashed rgba(0, 0, 0, 0.19); border-radius: 8px;
  cursor: pointer; background: rgba(0, 0, 0, 0.02); overflow: hidden; padding: 16px;
}
.picture-upload-box:hover { border-color: #00BD73; }
.logo-preview-img { width: 100%; height: 100%; object-fit: cover; }
.cursor-pointer { cursor: pointer; }
.add-image-btn { background-color: #00BD73 !important; color: #fff !important; }
/* Status pills reused from BuyingStationList.vue */
.green-button { background-color: #d4edda !important; border: 2px solid #28a745 !important; color: #28a745 !important; }
.red-button { background-color: #f8d7da !important; border: 2px solid #dc3545 !important; color: #dc3545 !important; }
.primary-user-label {
  display: inline-block;
  color: $primary-color;
  font-size: 11px;
  font-weight: 600;
}
::v-deep .owner-row {
  border-left: 4px solid #00BD73;
  background-color: rgba(17, 116, 67, 0.05);
}
::v-deep .v-data-table-header {
  background-color: $secondary-color !important;
}
::v-deep .v-data-table-header th {
  color: white !important;
}
::v-deep .v-data-table-header span,
::v-deep .v-data-table-header th {
  text-transform: uppercase !important;
  color: white !important;
}
</style>
